import { BrowserRouter as Router, Routes, Route } from 'react-router-dom'
import { ConfigProvider } from './contexts/ConfigContext'
import { DataProvider } from './contexts/DataContext'
import { LocationProvider } from './contexts/LocationContext'
import { FilterProvider } from './contexts/FilterContext'
import { ViewProvider } from './contexts/ViewContext'
import { NarrativeProvider } from './contexts/NarrativeContext'
import TraditionalDashboardLayout from './components/layout/TraditionalDashboardLayout'
import AiDashboardLayout from './components/layout/AiDashboardLayout'
import NarrativeDashboardLayout from './components/layout/NarrativeDashboardLayout'
import DatasetsLayout from './components/layout/DatasetsLayout'
import ScrollToTop from './components/shared/ScrollToTop'
import LandingPage from './pages/LandingPage'
import Dashboard from './views/Dashboard'
import DatasetsPage from './views/DatasetsPage'
import AiDashboard from './pages/AiDashboard'
import NarrativeDashboard from './pages/NarrativeDashboard'
import * as echarts from 'echarts'

echarts.registerTheme('ug-dashboard', {
  color: [
    '#ef233c',
    '#c10e66',
    '#7f2a74',
    '#3b2f65',
    '#0b2440',
    '#10b981',
    '#f59e0b',
    '#8b5cf6',
    '#06b6d4',
    '#f97316',
    '#84cc16',
    '#ec4899',
    '#6b7280',
  ],
  backgroundColor: '#fff',
  textStyle: { fontFamily: 'Poppins, system-ui, sans-serif' },
})

/**
 * App Component - Separated Dashboard Architecture
 *
 * Three independent dashboard experiences:
 * 1. Traditional Dashboard (/dashboard) - Full context providers + traditional layout
 * 2. AI Dashboard (/ai-dashboard) - Minimal contexts + AI-focused layout
 * 3. Narrative Dashboard (/narrative) - Narrative context + story-focused layout
 *
 * Each dashboard has its own layout and only loads necessary contexts
 */
function App() {
  return (
    <Router>
      <ScrollToTop />
      <ConfigProvider>
        <DataProvider>
          <Routes>
            {/* Landing Page - No layout, minimal contexts */}
            <Route path="/" element={<LandingPage />} />

            {/* Traditional Dashboard - Full contexts */}
            <Route
              path="/dashboard"
              element={
                <LocationProvider>
                  <FilterProvider>
                    <ViewProvider>
                      <TraditionalDashboardLayout>
                        <Dashboard />
                      </TraditionalDashboardLayout>
                    </ViewProvider>
                  </FilterProvider>
                </LocationProvider>
              }
            />
            <Route
              path="/dashboard/:view"
              element={
                <LocationProvider>
                  <FilterProvider>
                    <ViewProvider>
                      <TraditionalDashboardLayout>
                        <Dashboard />
                      </TraditionalDashboardLayout>
                    </ViewProvider>
                  </FilterProvider>
                </LocationProvider>
              }
            />

            {/* AI Dashboard - Needs LocationProvider for query context */}
            <Route
              path="/ai-dashboard"
              element={
                <LocationProvider>
                  <AiDashboardLayout>
                    <AiDashboard />
                  </AiDashboardLayout>
                </LocationProvider>
              }
            />

            {/* Narrative Dashboard - Narrative context only */}
            <Route
              path="/narrative"
              element={
                <NarrativeProvider>
                  <NarrativeDashboardLayout>
                    <NarrativeDashboard />
                  </NarrativeDashboardLayout>
                </NarrativeProvider>
              }
            />
            <Route
              path="/narrative/:locationId"
              element={
                <NarrativeProvider>
                  <NarrativeDashboardLayout>
                    <NarrativeDashboard />
                  </NarrativeDashboardLayout>
                </NarrativeProvider>
              }
            />
            <Route
              path="/narrative/:locationId/:storyId"
              element={
                <NarrativeProvider>
                  <NarrativeDashboardLayout>
                    <NarrativeDashboard />
                  </NarrativeDashboardLayout>
                </NarrativeProvider>
              }
            />

            {/* Datasets Page - Simple layout with header only */}
            <Route
              path="/datasets"
              element={
                <DatasetsLayout>
                  <DatasetsPage />
                </DatasetsLayout>
              }
            />
          </Routes>
        </DataProvider>
      </ConfigProvider>
    </Router>
  )
}

export default App
